<template>
  <div class="read-model-card" :style="{ minHeight: cardHeight }">
    <div
      class="card-item"
      :style="{ 'border-color': cardColor }"
    >
      <div class="d-flex align-center mb-1">
        <quick-rename-input
          :value="schema.name"
          @onChange="onBlurName"
          density="compact"
          :max-length="30"
          class="flex-grow-1 font-weight-medium"
          :disabled="isWorkflowDisabled"
        />
        <v-btn
          v-if="!isWorkflowDisabled"
          size="small"
          width="20"
          min-width="20"
          height="20"
          variant="plain"
          class="ml-2 pa-0"
          @click="emit('editSchema', schema.id)"
        >
          <v-icon size="14">mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          v-if="!isWorkflowDisabled"
          size="small"
          width="20"
          min-width="20"
          height="20"
          variant="plain"
          class="pa-0"
          :loading="deleting"
          @click="emit('deleteSchema', schema.id)"
        >
          <v-icon size="14">mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>

      <div
        ref="descriptionRef"
        class="schema-description mb-2"
        :class="{
          'schema-description--empty': !schema.description,
          'schema-description--invalid': descriptionInvalid
        }"
        :contenteditable="!isWorkflowDisabled"
        :data-placeholder="isWorkflowDisabled ? '' : 'Add a short description…'"
        spellcheck="false"
        @input="checkDescriptionLength"
        @paste="onPasteDescription"
        @blur="onBlurDescription"
        @keydown.enter.prevent="$event.target.blur()"
        @keydown.esc="restoreDescription($event)"
      >
        {{ schema.description || '' }}
      </div>

      <div class="mb-1 entity-select-wrapper d-flex align-center justify-space-between ga-2">
        <select-entity
          :entityId="schema.entityId"
          @onSelectEntity="onSelectEntity"
        >
          <v-chip
            density="compact"
            size="small"
            class="cursor-pointer entity-chip"
            :disabled="isWorkflowDisabled && !linkedEntity"
          >
            <v-icon start size="12">mdi-database</v-icon>
            <span>
              {{ linkedEntity ? linkedEntity.name : 'Select Entity' }}
            </span>
          </v-chip>
        </select-entity>
        <toggle
          mandatory
          class="cardinality-toggle"
          :value="schema.cardinality || 'one-to-many'"
          :options="QUERY_CARDINALITY_OPTIONS"
          :disabled="isWorkflowDisabled"
          @update:modelValue="(val) => setReadModelCardinality(schema.id, val)"
        />
      </div>

      <read-model-field-rows
        v-if="(schema.fields ?? []).length"
        class="py-1 fields"
        :fields="schema.fields ?? []"
        :depth="1"
        :maxDepth="3"
        :path="[]"
        :schema="schema"
        :isWorkflowDisabled="isWorkflowDisabled"
      />

      <div
        class="d-flex align-center"
        :class="{
          'justify-space-between': usageStatus === 'unused',
          'justify-start': usageStatus !== 'unused',
          'mb-2': linkedDomainEvents.length || !isWorkflowDisabled
        }"
      >
        <v-tooltip v-if="!isWorkflowDisabled" location="bottom" open-delay="100">
          <template v-slot:activator="{ on, props }">
            <v-btn
              v-bind="props"
              v-on="{ ...on }"
              size="small"
              width="16"
              min-width="16"
              height="16"
              variant="plain"
              @click="onAddField"
              tabindex="-1"
              class="pa-0"
            >
              <v-icon size="16">mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Add field</span>
        </v-tooltip>
        <v-spacer v-if="usageStatus === 'unused'" />
        <v-tooltip v-if="usageStatus === 'unused'" location="bottom" open-delay="300">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="unused-badge">
              <v-icon size="12" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>Unused</span>
            </div>
          </template>
          <span>Not used by any card in the domain model</span>
        </v-tooltip>
      </div>

      <div class="domain-event-links">
        <div class="d-flex flex-wrap ga-1 align-center">
          <v-chip
            v-for="event in linkedDomainEvents"
            :key="event.id"
            density="compact"
            size="small"
            class="domain-event-chip"
            :closable="!isWorkflowDisabled"
            variant="flat"
            :style="{ backgroundColor: domainEventBgColor, color: 'var(--text-dark)' }"
          >
            <v-icon start size="small">mdi-note-outline</v-icon>
            {{ event.name }}
            <template v-if="!isWorkflowDisabled" #close>
              <v-icon
                icon="mdi-close-circle"
                color="#888"
                :size="16"
                @click.stop="removeDomainEventLink(schema.id, event.id)"
              />
            </template>
          </v-chip>
          <v-tooltip
            v-for="suggested in suggestedDomainEvents"
            :key="'suggested-' + suggested.id"
            location="bottom"
            open-delay="300"
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <v-chip
                v-bind="tooltipProps"
                density="compact"
                size="small"
                class="suggested-event-chip cursor-pointer"
                variant="outlined"
                :style="{ borderColor: domainEventCardColor, color: domainEventCardColor }"
                @click="addDomainEventLink(schema.id, suggested.id)"
              >
                <v-icon start size="12">mdi-note-outline</v-icon>
                {{ suggested.name }}
                <v-icon end size="12">mdi-plus</v-icon>
              </v-chip>
            </template>
            <span>Suggested based on shared entity. Click to link this domain event as an update trigger for this read model.</span>
          </v-tooltip>
          <v-menu v-if="hasAvailableDomainEvents" :close-on-content-click="true">
            <template v-slot:activator="{ props }">
              <v-chip
                v-bind="props"
                density="compact"
                size="small"
                class="cursor-pointer domain-event-chip"
                :style="{ color: domainEventCardColor }"
              >
                <v-icon start size="12">mdi-note-outline</v-icon>
                + Domain Event
              </v-chip>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="event in availableDomainEvents"
                :key="event.id"
                @click="addDomainEventLink(schema.id, event.id)"
              >
                <v-list-item-title>{{ event.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import QuickRenameInput from '@/components/QuickRenameInput'
import ReadModelFieldRows from '@/components/ReadModelFieldRows.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import copyPasteHelpers from '@/helpers/copyPasteHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { useSortFields } from '@/composables/useSortFields'
import useReadModelDomainEvents from '@/composables/useReadModelDomainEvents'
import useReadModelActions from '@/composables/useReadModelActions'
import {
  SchemaType,
  QUERY_CARDINALITY_OPTIONS,
  MAX_DESCRIPTION_LENGTH
} from '@/constants/schema'

const { schema, usageStatus, deleting } = defineProps({
  schema: Object,
  usageStatus: String,
  deleting: Boolean
})
const emit = defineEmits(['deleteSchema', 'editSchema'])

const store = useStore()
const {
  cardTypeColor,
  getQueryCardHeight,
  selectSourceEntity,
  addSchemaField
} = useDomainModel()
const {
  getDomainEventsForReadModel,
  getAvailableDomainEventsForReadModel,
  getSuggestedDomainEventsForReadModel
} = useReadModelDomainEvents()
const {
  blurReadModelName,
  blurReadModelDescription,
  setReadModelCardinality,
  addDomainEventLink,
  removeDomainEventLink
} = useReadModelActions()

const { initSortable, destroySortable } = useSortFields()

const descriptionInvalid = ref(false)
const descriptionLength = ref(0)
const descriptionRef = ref(null)
const descriptionHeight = ref(0)
let descriptionResizeObserver = null

const workflow = computed(() => store.state.workflow.workflow)
const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))
const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))
const cardColor = computed(() => cardTypeColor(queryCardType.value) || '#eeeeee')
const domainEventCardColor = computed(() => cardTypeColor(domainEventCardType.value) || '#eeeeee')
const linkedDomainEvents = computed(() => getDomainEventsForReadModel(schema))
const availableDomainEvents = computed(() => getAvailableDomainEventsForReadModel(schema))
const suggestedDomainEvents = computed(() => getSuggestedDomainEventsForReadModel(schema))
const hasAvailableDomainEvents = computed(() => !isWorkflowDisabled.value && availableDomainEvents.value.length > 0)
const cardHeight = computed(() => {
  return getQueryCardHeight(schema, linkedDomainEvents, suggestedDomainEvents, hasAvailableDomainEvents, descriptionHeight.value)
})

const domainEventBgColor = computed(() => {
  const color = domainEventCardType.value?.color
  return color ? `rgba(${color.red},${color.green},${color.blue},0.15)` : 'transparent'
})

const linkedEntity = computed(() => {
  if (!schema.entityId) {
    return null
  }

  return (workflow.value.schemas ?? []).find((s) => s.id === schema.entityId && s.type === SchemaType.ENTITY)
})

const checkDescriptionLength = (event) => {
  descriptionLength.value = (event.target.innerText || '').length
  descriptionInvalid.value = descriptionLength.value > MAX_DESCRIPTION_LENGTH
}

const onPasteDescription = (event) => {
  copyPasteHelpers.customPasteHandler(event)
  checkDescriptionLength(event)
}

const onBlurDescription = async (event) => {
  const newDescription = (event.target.innerText || '').trim()
  const currentDescription = schema.description || ''

  if (newDescription.length > MAX_DESCRIPTION_LENGTH) {
    store.commit('snackbar/showMessage', {
      content: `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
      timeout: 4000,
      color: 'red'
    }, { root: true })
    event.target.innerText = currentDescription
    descriptionInvalid.value = false

    return
  }

  descriptionInvalid.value = false
  await blurReadModelDescription(event, schema.id)
}

const restoreDescription = (event) => {
  event.target.innerText = schema.description || ''
  descriptionInvalid.value = false
  event.target.blur()
}

const onBlurName = (e) => {
  blurReadModelName(e, schema.id)
}

const onSelectEntity = (entity) => {
  selectSourceEntity(schema.id, entity?.id || null)
}

const onAddField = (e) => {
  addSchemaField(schema.id)
  e.target.closest('button')?.blur()
}

watch(() => schema.fields, () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      initSortable()
    })
  })
}, { deep: true })

onMounted(() => {
  initSortable()
  if (descriptionRef.value) {
    descriptionHeight.value = descriptionRef.value.offsetHeight
    descriptionResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        descriptionHeight.value = descriptionRef.value?.offsetHeight || 0
      })
    })
    descriptionResizeObserver.observe(descriptionRef.value)
  }
})

onBeforeUnmount(() => {
  destroySortable()
  descriptionResizeObserver?.disconnect()
  descriptionResizeObserver = null
})
</script>

<style scoped>
.card-item {
  min-width: 200px;
  max-width: 300px;
  overflow: hidden;
  padding: 12px;
  border-radius: 8px;
  background: var(--light-1);
  border: 1px solid black;
  border-top-width: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  line-height: 1;
}

.entity-select-wrapper {
  font-size: 0;
}

.schema-description {
  min-height: 16px;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 11px;
  line-height: 18px;
  color: var(--text-light);
  outline: none;
  transition: border-color 0.2s;
  white-space: pre-wrap;
  word-break: break-word;
}

.schema-description:focus {
  background-color: var(--light-2);
}

.schema-description--empty:not(:focus)::before {
  content: attr(data-placeholder);
  color: var(--dark-1);
  pointer-events: none;
}

.schema-description--invalid,
.schema-description--invalid:focus {
  border-color: #ED1C0C;
}

:deep(.entity-chip.v-chip) {
  height: 16px;
  font-size: 11px;
}

.cardinality-toggle :deep(.v-btn-group.v-btn-toggle .v-btn) {
  padding-top: 0;
  padding-bottom: 0;
}

:deep(.domain-event-chip.v-chip) {
  height: 16px;
  font-size: 11px;
}

:deep(.suggested-event-chip.v-chip) {
  height: 16px;
  font-size: 11px;
  border-style: dashed;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

.unused-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  border-radius: 12px;
  font-size: 11px;
  white-space: nowrap;
  background-color: #FFF3E0;
  color: #E65100;
  height: 16px;
}

</style>
